/**
 * Where a page load lands.
 *
 * Home is the default front door, but it must never get between someone and a
 * link they followed. Three rules, in order:
 *
 * 1. A clean application path or explicit legacy `?section=` wins.
 * 2. Any legacy query string at all means the URL carries a view (or preview
 *    flags), so the app opens where that view lives: Explore. Landing on Home
 *    and making them click through would lose the point of the link.
 * 3. Otherwise the stored preference decides, and its default is Home.
 *
 * The preference is per browser, like page visibility and the sidebar state -
 * this is "where I like to start", not an account setting.
 */

import type { Section } from './store.ts';
import { parseAppRoute, sectionRoute } from './app-route.ts';

export const START_SECTION_KEY = 'neptune_start_section_v1';

export type StartSection = 'home' | 'explore';

export function startSection(): StartSection {
  try {
    return localStorage.getItem(START_SECTION_KEY) === 'explore' ? 'explore' : 'home';
  } catch {
    return 'home';
  }
}

export function setStartSection(value: StartSection): void {
  try {
    localStorage.setItem(START_SECTION_KEY, value);
  } catch {
    // A browser refusing storage is not worth failing a click over; the
    // preference simply does not stick.
  }
}

/** The section a load with this path/query should open on. */
export function initialSection(search: string, pathname = '/'): Section {
  const route = parseAppRoute(pathname);
  if (route) return route.section;
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  const explicit = params.get('section');
  if (
    explicit === 'home' ||
    explicit === 'explore' ||
    explicit === 'dashboards' ||
    explicit === 'catalog' ||
    explicit === 'assistant'
  ) {
    return explicit;
  }
  const registered = sectionRoute(explicit);
  if (registered) return registered.section;
  if ([...params.keys()].length > 0) return 'explore';
  return startSection();
}
